import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Lightbulb, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const CATEGORIES = [
  { value: 'ui', label: 'UI / Diseño' },
  { value: 'batallas', label: 'Batallas' },
  { value: 'eventos', label: 'Eventos' },
  { value: 'premios', label: 'Premios' },
  { value: 'reconocimientos', label: 'Reconocimientos' },
  { value: 'monetizacion', label: 'Monetización' },
  { value: 'otro', label: 'Otro' },
];

const PRIORITIES = [
  { value: 'baja', label: 'Baja', color: '#78D9A6' },
  { value: 'media', label: 'Media', color: '#00F0FF' },
  { value: 'alta', label: 'Alta', color: '#FF2E8B' },
];

const STATUS_META = {
  new:         { label: 'Nueva',         color: '#00F0FF' },
  reviewed:    { label: 'Revisada',      color: '#B026FF' },
  planned:     { label: 'Planeada',      color: '#FFD166' },
  in_progress: { label: 'En progreso',   color: '#F97316' },
  done:        { label: 'Implementada',  color: '#00FFA3' },
  rejected:    { label: 'Rechazada',     color: '#EF4444' },
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function SuggestionsPage() {
  const nav = useNavigate();
  const { user } = useAuth();

  const isAllowed = useMemo(() => {
    const r = new Set(user?.roles || []);
    return r.has('organizer') || r.has('admin');
  }, [user]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'otro', priority: 'media' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/suggestions/mine');
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error('Solo organizadores o admin');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAllowed) load(); else setLoading(false); }, [isAllowed]);

  const submit = async () => {
    if (form.title.trim().length < 5) {
      toast.error('El título debe tener al menos 5 caracteres');
      return;
    }
    if (form.description.trim().length < 20) {
      toast.error('La descripción debe tener al menos 20 caracteres');
      return;
    }
    setSaving(true);
    try {
      await api.post('/suggestions', {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        priority: form.priority,
      });
      toast.success('¡Sugerencia enviada!');
      setForm({ title: '', description: '', category: 'otro', priority: 'media' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando sugerencia');
    } finally { setSaving(false); }
  };

  if (!isAllowed) {
    return (
      <div className="min-h-screen bg-aura-bg pb-28 relative">
        <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
        <header className="relative flex items-center justify-between px-5 pt-6">
          <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
            <ChevronLeft size={20} />
          </button>
          <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">SUGERENCIAS</div>
          <span className="w-8" />
        </header>
        <main className="relative mx-auto max-w-md px-5 pt-10">
          <div className="aura-card p-6 text-center" data-testid="no-role">
            <Lightbulb size={40} className="mx-auto text-aura-yellow mb-2" />
            <div className="font-display font-black text-white text-lg">Solo Organizadores</div>
            <p className="text-sm text-aura-textDim mt-2">
              Solicita el rol de <span className="text-aura-cyan">organizer</span> para enviar sugerencias al equipo AURA.
            </p>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative" data-testid="suggestions-page">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-aura-yellow/15 blur-3xl" />

      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} />
        </button>
        <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">SUGERENCIAS</div>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-4">
        <section className="aura-card p-4 space-y-3" data-testid="suggestion-form">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-aura-yellow" />
            <div className="font-display font-black text-white tracking-wider text-sm">PROPÓN UNA IDEA</div>
          </div>
          <input
            data-testid="in-title"
            placeholder="Título breve (5–140 caracteres) *"
            value={form.title}
            maxLength={140}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60"
          />
          <textarea
            data-testid="in-desc"
            placeholder="Describe la idea o mejora (mínimo 20 caracteres) *"
            value={form.description}
            maxLength={2000}
            rows={4}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60"
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              data-testid="in-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60"
            >
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select
              data-testid="in-priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60"
            >
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>Prioridad {p.label}</option>)}
            </select>
          </div>
          <button
            data-testid="btn-submit-suggestion"
            onClick={submit}
            disabled={saving}
            className="aura-btn-primary w-full !py-3 justify-center"
          >
            <Send size={14} /> {saving ? 'Enviando…' : 'Enviar sugerencia'}
          </button>
        </section>

        <div className="text-[11px] uppercase tracking-[0.3em] text-aura-textDim pl-1">
          Mis sugerencias enviadas
        </div>

        {loading ? (
          <div className="text-center py-16 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>
        ) : items.length === 0 ? (
          <div className="aura-card p-8 text-center text-sm text-aura-textDim" data-testid="suggestions-empty">
            Aún no envías sugerencias. La primera cuenta triple :)
          </div>
        ) : (
          <ul className="space-y-3" data-testid="suggestions-list">
            {items.map((s) => (
              <li key={s.id} className="aura-card p-4" data-testid={`sug-item-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border"
                        style={{ color: STATUS_META[s.status]?.color, borderColor: `${STATUS_META[s.status]?.color}55` }}
                      >
                        {STATUS_META[s.status]?.label || s.status}
                      </span>
                      <span className="text-[9px] font-mono uppercase tracking-widest text-aura-textDim">
                        {CATEGORIES.find(c => c.value === s.category)?.label || s.category}
                      </span>
                      <span
                        className="text-[9px] font-mono uppercase tracking-widest"
                        style={{ color: PRIORITIES.find(p => p.value === s.priority)?.color }}
                      >
                        ● {s.priority}
                      </span>
                    </div>
                    <div className="font-display font-black text-white mt-1.5">{s.title}</div>
                    <p className="text-xs text-aura-textDim mt-1 whitespace-pre-wrap">{s.description}</p>
                    {s.admin_notes && (
                      <div className="mt-3 p-2.5 rounded-lg bg-aura-cyan/5 border border-aura-cyan/25 text-xs text-white/80">
                        <div className="text-[9px] uppercase tracking-widest text-aura-cyan mb-1">Nota del equipo</div>
                        {s.admin_notes}
                      </div>
                    )}
                    <div className="mt-2 text-[10px] font-mono text-aura-textDim">{fmtDate(s.created_at)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
